import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Permission, SessionView, StoreView } from '@wodi/shared';
import {
  Button,
  buttonLinkProps,
  EmptyState,
  LoadingBlock,
  type NavIconName,
  Notice,
  Tag,
} from '@wodi/ui';
import type { ComponentType } from 'react';

import { ApiError } from '../api/client.ts';
import { billsQuery } from '../api/billing.ts';
import { setupStatusQuery, unpricedItemsQuery } from '../api/home.ts';
import { can } from '../api/session.ts';
import { expiringQuery, reorderQuery, storesQuery } from '../api/stock.ts';
import { queuesQuery } from '../api/triage.ts';
import { cents, money } from '../billing/format.ts';
import { formatWaiting } from '../format/waiting.ts';
import { StaffLicencesSection } from '../staff/home-section.tsx';
import { openPatientSearch } from '../patients/patient-search-palette.tsx';
import styles from './home.module.css';
import { plural, SETUP_COPY } from './setup-items.ts';
import { TODAY_EMPTY, TodayList, todaySummary, TodayVisitsState } from './today-visits.tsx';

// Home is a list of sections, each shown only to people whose permissions let them act
// on it (never by role name). To add one from another slice (the lab worklist, …):
//   1. Write a component that loads its own data and handles its loading, empty, error
//      and offline states (see StockSection).
//   2. Append { id, title, visible, Component } to HOME_SECTIONS below, where `visible`
//      checks the same permissions the section's API routes require.
// Order is the order on screen; nothing else needs to change.

export interface HomeSection {
  id: string;
  /** The section heading. */
  title: string;
  /** The line icon in the heading's primary-tint chip (decoration; the title names it). */
  icon: NavIconName;
  visible: (session: SessionView) => boolean;
  Component: ComponentType<{ session: SessionView }>;
}

const SETUP_PERMISSIONS: readonly Permission[] = [
  'admin.facilities.manage',
  'catalogue.read',
  'stock.manage',
  'admin.users.read',
  'terminology.read',
];

function failure(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function RetryNotice({ message, retry }: { message: string; retry: () => void }) {
  return (
    <Notice
      tone="error"
      action={
        <Button dense onPress={retry}>
          Try again
        </Button>
      }
    >
      {message}
    </Notice>
  );
}

// --- Front desk --------------------------------------------------------------------------

function FrontDeskSection({ session }: { session: SessionView }) {
  const seesToday = can(session, 'visits.read') && can(session, 'patients.read');
  return (
    <>
      <div className={styles.actions}>
        {can(session, 'visits.open') ? (
          <Link to="/reception" {...buttonLinkProps()}>
            Check in a patient
          </Link>
        ) : null}
        {can(session, 'patients.read') ? (
          <Button onPress={openPatientSearch} aria-keyshortcuts="Control+K">
            Find patient (Ctrl K)
          </Button>
        ) : null}
      </div>
      {seesToday ? (
        <TodayVisitsState limit={5}>
          {(view) =>
            view.items.length === 0 ? (
              <>
                <EmptyState>{TODAY_EMPTY}</EmptyState>
                {todaySummary(view) === '' ? null : (
                  <p className={styles.muted}>{todaySummary(view)}</p>
                )}
              </>
            ) : (
              <>
                <p className={styles.summary}>{todaySummary(view)}</p>
                <TodayList visits={view.items} label="Latest arrivals today" />
                {can(session, 'visits.open') && view.counts.total > view.items.length ? (
                  <Link to="/reception">See all of today's arrivals at Reception</Link>
                ) : null}
              </>
            )
          }
        </TodayVisitsState>
      ) : null}
    </>
  );
}

// --- Setup checklist ---------------------------------------------------------------------

function SetupSection() {
  const status = useQuery(setupStatusQuery);
  if (status.isPending) return <LoadingBlock label="Loading setup" rows={4} />;
  if (status.isError)
    return (
      <RetryNotice
        message={failure(status.error, "Setup status couldn't be loaded. Try again.")}
        retry={() => void status.refetch()}
      />
    );
  const { items } = status.data;
  const done = items.filter((i) => i.state === 'done').length;
  return (
    <>
      <p className={styles.summary}>
        {done === items.length
          ? 'Everything on this list is set up.'
          : `${String(done)} of ${String(items.length)} set up. The rest are listed with what is missing.`}
      </p>
      <ul className={styles.list} aria-label="Setup checklist">
        {items.map((item) => {
          const copy = SETUP_COPY[item.key];
          return (
            <li key={item.key} className={styles.listRow}>
              <span className={styles.listLabel}>
                {copy.to === null ? copy.label : <Link to={copy.to}>{copy.label}</Link>}
              </span>
              <span>{copy.describe(item)}</span>
              <Tag state={item.state === 'done' ? 'done' : 'pending'}>
                {item.state === 'done' ? 'Done' : 'To do'}
              </Tag>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// --- Stock ---------------------------------------------------------------------------------

function StoreAlerts({ store }: { store: StoreView }) {
  const expiring = useQuery(expiringQuery(store.id));
  const reorder = useQuery(reorderQuery(store.id));
  let text: string;
  let needsAction = false;
  if (expiring.isPending || reorder.isPending) text = 'Checking stock…';
  else if (expiring.isError || reorder.isError)
    text = "Stock alerts couldn't be loaded. Open the store to see its stock.";
  else {
    const expired = expiring.data.filter((r) => r.status === 'expired').length;
    const near = expiring.data.length - expired;
    const low = reorder.data.length;
    const parts = [
      ...(expired > 0 ? [`${plural(expired, 'batch', 'batches')} expired`] : []),
      ...(near > 0 ? [`${plural(near, 'batch', 'batches')} expiring within 90 days`] : []),
      ...(low > 0 ? [`${plural(low, 'item')} at or below reorder level or out of stock`] : []),
    ];
    needsAction = parts.length > 0;
    text = needsAction ? `${parts.join(', ')}.` : 'Nothing expiring or running low.';
  }
  return (
    <li className={styles.listRow}>
      <span className={styles.listLabel}>
        <Link to="/stores" search={{ storeId: store.id }}>
          {store.name}
        </Link>
      </span>
      <span>{text}</span>
      <Tag state={needsAction ? 'attention' : 'done'}>{needsAction ? 'Check' : 'OK'}</Tag>
    </li>
  );
}

function StockSection({ session }: { session: SessionView }) {
  const stores = useQuery(storesQuery);
  if (stores.isPending) return <LoadingBlock label="Loading stores" rows={2} />;
  if (stores.isError)
    return (
      <RetryNotice
        message={failure(stores.error, "Stores couldn't be loaded. Try again.")}
        retry={() => void stores.refetch()}
      />
    );
  if (stores.data.length === 0)
    return (
      <EmptyState>
        {can(session, 'stock.manage')
          ? 'No stores at this facility yet. Add one in Settings.'
          : 'No stores at this facility yet. Ask an administrator to set them up.'}
      </EmptyState>
    );
  return (
    <ul className={styles.list} aria-label="Stock alerts by store">
      {stores.data.map((store) => (
        <StoreAlerts key={store.id} store={store} />
      ))}
    </ul>
  );
}

// --- Prices ----------------------------------------------------------------------------------

function PricesSection() {
  const unpriced = useQuery(unpricedItemsQuery);
  if (unpriced.isPending) return <LoadingBlock label="Loading prices" rows={2} />;
  if (unpriced.isError)
    return (
      <RetryNotice
        message={failure(unpriced.error, "Prices couldn't be checked. Try again.")}
        retry={() => void unpriced.refetch()}
      />
    );
  const { count, items } = unpriced.data;
  if (count === 0)
    return <p className={styles.summary}>Every service and item offered here has a cash price.</p>;
  return (
    <>
      <p className={styles.summary}>
        {`${plural(count, 'service or item', 'services and items')} offered here ${count === 1 ? 'has' : 'have'} no cash price. Charges for ${count === 1 ? 'it' : 'them'} stop at "Not priced" until a price is set.`}
      </p>
      <ul className={styles.list} aria-label="Items without a cash price">
        {items.map((item) => (
          <li key={item.id} className={styles.listRow}>
            <span className={`${styles.listLabel} wodi-id`}>{item.code}</span>
            <span>
              <Link to="/settings/services/$itemId" params={{ itemId: item.id }}>
                {item.name}
              </Link>
            </span>
            <Tag state="attention">Not priced</Tag>
          </li>
        ))}
      </ul>
      {count > items.length ? (
        <Link to="/settings/services">See all in Services and prices</Link>
      ) : null}
    </>
  );
}

// --- Queues ----------------------------------------------------------------------------------

function QueuesSection() {
  const queues = useQuery(queuesQuery);
  if (queues.isPending) return <LoadingBlock label="Loading queues" rows={2} />;
  if (queues.isError)
    return (
      <RetryNotice
        message={failure(queues.error, "Queues couldn't be loaded. Try again.")}
        retry={() => void queues.refetch()}
      />
    );
  if (queues.data.length === 0)
    return (
      <EmptyState>
        No queues at this facility yet. An administrator adds them in Settings.
      </EmptyState>
    );
  const waiting = queues.data.reduce((sum, q) => sum + q.waiting, 0);
  return (
    <>
      <p className={styles.summary}>
        {waiting === 0 ? 'Nobody is waiting.' : `${plural(waiting, 'patient')} waiting.`}
      </p>
      <ul className={styles.list} aria-label="Queues">
        {queues.data.map((queue) => (
          <li key={queue.id} className={styles.listRow}>
            <span className={styles.listLabel}>
              <Link to="/queues" search={{ servicePointId: queue.id }}>
                {queue.name}
              </Link>
            </span>
            <span className="wodi-num">
              {queue.waiting === 0 || queue.longestWaitMinutes === null
                ? 'Nobody waiting'
                : `${plural(queue.waiting, 'waiting', 'waiting')}, longest ${formatWaiting(queue.longestWaitMinutes)}`}
            </span>
            <Tag state={queue.waiting === 0 ? 'done' : 'pending'}>
              {queue.waiting === 0 ? 'Clear' : 'Waiting'}
            </Tag>
          </li>
        ))}
      </ul>
    </>
  );
}

// --- Unpaid bills -------------------------------------------------------------------------------

const BILLS_SHOWN = 5;

function UnpaidBillsSection() {
  const bills = useQuery(billsQuery('owing'));
  if (bills.isPending) return <LoadingBlock label="Loading bills" rows={2} />;
  if (bills.isError)
    return (
      <RetryNotice
        message={failure(bills.error, "Bills couldn't be loaded. Try again.")}
        retry={() => void bills.refetch()}
      />
    );
  const { items } = bills.data;
  if (items.length === 0)
    return <p className={styles.summary}>No bills are waiting for payment.</p>;
  const total = items.reduce((sum, b) => sum + cents(b.owed), 0n);
  const more = bills.data.nextCursor !== null;
  return (
    <>
      <p className={styles.summary}>
        {`${more ? 'More than ' : ''}${plural(items.length, 'bill')} waiting for payment, ${money({ currency: 'KES', cents: total.toString() })} in total${more ? ' so far' : ''}.`}
      </p>
      <ul className={styles.list} aria-label="Bills waiting for payment">
        {items.slice(0, BILLS_SHOWN).map((bill) => (
          <li key={bill.visitId} className={styles.listRow}>
            <span className={styles.stack}>
              <Link to="/cashier" search={{ visitId: bill.visitId }}>
                {bill.patient.name}
              </Link>
              <span className={`${styles.muted} wodi-id`}>{bill.patient.patientNumber}</span>
            </span>
            <span className="wodi-num">{money(bill.owed)}</span>
            <Tag state={bill.unpricedCount > 0 ? 'attention' : 'pending'}>
              {bill.unpricedCount > 0 ? 'Not priced' : 'To pay'}
            </Tag>
          </li>
        ))}
      </ul>
      {items.length > BILLS_SHOWN ? <Link to="/cashier">See all at the cashier</Link> : null}
    </>
  );
}

export const HOME_SECTIONS: readonly HomeSection[] = [
  {
    id: 'front-desk',
    title: 'Front desk',
    icon: 'reception',
    visible: (s) => can(s, 'visits.open') || can(s, 'patients.read'),
    Component: FrontDeskSection,
  },
  {
    id: 'queues',
    title: 'Queues',
    icon: 'queue',
    visible: (s) => can(s, 'queues.read'),
    Component: QueuesSection,
  },
  {
    id: 'unpaid-bills',
    title: 'Unpaid bills',
    icon: 'cashier',
    visible: (s) => can(s, 'billing.read'),
    Component: UnpaidBillsSection,
  },
  {
    id: 'stock',
    title: 'Stock',
    icon: 'stores',
    visible: (s) => can(s, 'stock.read'),
    Component: StockSection,
  },
  {
    id: 'prices',
    title: 'Prices',
    icon: 'approvals',
    visible: (s) => can(s, 'catalogue.read'),
    Component: PricesSection,
  },
  {
    // Staff records with licence expiry (docs/plans/phase2-staff-records.md §6).
    id: 'staff-licences',
    title: 'Staff licences',
    icon: 'staff',
    visible: (s) => can(s, 'staff.read'),
    Component: StaffLicencesSection,
  },
  {
    id: 'setup',
    title: 'Set up Wodi',
    icon: 'settings',
    visible: (s) => SETUP_PERMISSIONS.some((p) => can(s, p)),
    Component: SetupSection,
  },
];
